import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { take, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Browser } from '@capacitor/browser';

import * as rootReducer from 'app/core/store';
import * as fromActions from 'app/core/store/actions/user.actions';
import * as fromRouterSelectors from 'app/core/store/selectors/router.selectors';
import { UserState } from 'app/core/store/reducers/user.reducer';
import { PlatformService } from 'app/core/services/platform.service';
import { ToastService } from 'app/core/services/toast.service';
import { VALIDATION_REGEXP } from 'app/shared/constants/app-service-constants';
import { ValidationMessages } from './modal-registration.constants';
import { FormValidator } from '../form-validator';

@Component({
  standalone: false,
  selector: 'pptl-registration',
  templateUrl: './modal-registration.component.html',
  styleUrls: ['./modal-registration.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModalRegistrationComponent implements OnDestroy {
  registrationFormGroup: FormGroup;
  accountFormGroup: FormGroup;
  name = new FormControl('');
  lastName = new FormControl('');
  email = new FormControl('');
  password = new FormControl('');
  cbTerms = new FormControl('');

  readonly validationMessages = ValidationMessages;
  public typePass = 'password';
  public typeRePass = 'password';
  public showPass = false;
  public showRePass = false;
  public disableBtns = false;
  public loaded = false;

  private subscrList: Subscription[] = [];

  constructor(
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    public modalController: ModalController,
    private store: Store<rootReducer.State>,
    private platformService: PlatformService,
  ) {
    this.accountFormGroup = this.formBuilder.group(
      {
        firstName: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(VALIDATION_REGEXP.AT_LEAST_ONE_LETTER),
          ]),
        ],
        lastName: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(VALIDATION_REGEXP.AT_LEAST_ONE_LETTER),
          ]),
        ],
        emailAddress: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(VALIDATION_REGEXP.SIMPLE_EMAIL),
          ]),
        ],
        companyName: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(VALIDATION_REGEXP.AT_LEAST_ONE_LETTER),
          ]),
        ],
        password: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(32),
            FormValidator.upperCase,
            FormValidator.lowerCase,
            FormValidator.digits,
            FormValidator.specialSymbol,
            FormValidator.noWhiteSpace,
          ]),
        ],
        rePassword: ['', Validators.required],
        referredByRep: [''],
        cbTerms: [false, Validators.requiredTrue],
      },
      {
        validator: FormValidator.passwordCompare.bind(this),
      },
    );

    this.registrationFormGroup = this.formBuilder.group({
      accountFormGroup: this.accountFormGroup,
    });

    const isDeviceAndBackButtonExists = !!this.platformService.getBackButtonEmitter();
    if (isDeviceAndBackButtonExists) {
      this.subscrList.push(
        this.platformService.getBackButtonEmitter().subscribeWithPriority(9999, () => {
          this.modalController.dismiss();
        }),
      );
    }

    this.store
      .pipe(select(fromRouterSelectors.getQueryParams), take(1))
      .subscribe((params: any) => {
        if (params?.sender) {
          this.accountFormGroup.patchValue({ referredByRep: params.sender });
        }
      });
  }

  ngOnDestroy() {
    this.subscrList.forEach((subscr: any) => subscr.unsubscribe());
  }

  onCloseButtonClick() {
    this.modalController.dismiss();
  }

  onSubmitButtonClick() {
    this.disableBtns = true;
    const { rePassword, ...user } = this.accountFormGroup.value;
    this.store.dispatch(new fromActions.CreateNewUser(user));
    this.processUserCreation();
  }

  showPassword() {
    this.showPass = !this.showPass;
    this.typePass = this.showPass ? 'text' : 'password';
  }

  showRePassword() {
    this.showRePass = !this.showRePass;
    this.typeRePass = this.showRePass ? 'text' : 'password';
  }

  getValidClass(controlName: string, errorTypeField: string): string {
    return FormValidator.getValidClass(controlName, errorTypeField, this.accountFormGroup);
  }

  private processUserCreation() {
    this.store
      .pipe(
        select(rootReducer.selectUser),
        filter((userState: UserState) => userState.loaded || !!userState.creationError),
        take(1),
      )
      .subscribe((userState: UserState) => {
        this.loaded = userState.loaded;
        this.disableBtns = false;
        if (userState.creationError) {
          this.toastService.showToast(userState.creationError);
          this.modalController.dismiss();
        }
      });
  }

  public openTerms(): void {
    const url = 'https://www.sonymusic.com/terms-and-conditions/';
    Browser.open({url});
  }

  public openPrivacy(): void {
    const url = 'https://www.sonymusic.com/privacy-policy/';
    Browser.open({url});
  }
}
